import { supabase } from '../lib/supabase';
import type { VersionTipo } from '../config/versionTypes';
import type { VersionArea } from '../config/versionAreas';

export interface Version {
  id: string;
  version_number: number;
  version_code: string;
  tipo: VersionTipo;
  descripcion: string;
  fecha: string;
  area: VersionArea;
  es_ruptura: boolean;
  created_at?: string;
  publicado?: boolean;
  commit_sha?: string | null;
}

export interface PaginatedVersionsResponse {
  data: Version[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface VersionFiltersParams {
  searchTerm?: string;
  tipos?: VersionTipo[];
  fechaInicio?: string;
  fechaFin?: string;
}

export interface UpdateVersionData {
  version_code?: string;
  tipo?: VersionTipo;
  descripcion?: string;
  fecha?: string;
  area?: VersionArea;
  es_ruptura?: boolean;
}

class VersionService {
  private parseVersionCode(versionCode: string): number[] {
    return versionCode.split('.').map(part => parseInt(part, 10) || 0);
  }

  private compareVersions(a: Version, b: Version): number {
    const partsA = this.parseVersionCode(a.version_code);
    const partsB = this.parseVersionCode(b.version_code);

    const maxLength = Math.max(partsA.length, partsB.length);

    for (let i = 0; i < maxLength; i++) {
      const partA = partsA[i] || 0;
      const partB = partsB[i] || 0;

      if (partA !== partB) {
        return partB - partA;
      }
    }

    return b.version_number - a.version_number;
  }

  async getVersions(offset: number = 0, limit: number = 20): Promise<Version[]> {
    const { data, error } = await supabase
      .from('versiones')
      .select('*')
      .eq('publicado', true)
      .order('version_number', { ascending: false });

    if (error) throw error;

    const sortedData = (data || []).sort((a, b) => this.compareVersions(a, b));

    return sortedData.slice(offset, offset + limit);
  }

  async getCurrentVersion(): Promise<Version | null> {
    const { data, error } = await supabase
      .from('versiones')
      .select('*')
      .eq('publicado', true);

    if (error) return null;
    if (!data || data.length === 0) return null;

    const sortedData = data.sort((a, b) => this.compareVersions(a, b));
    return sortedData[0];
  }

  async getTotalCount(): Promise<number> {
    const { count, error } = await supabase
      .from('versiones')
      .select('*', { count: 'exact', head: true })
      .eq('publicado', true);

    if (error) return 0;

    return count || 0;
  }

  normalizeVersionCode(v: string): string {
    const parts = v.split('.').map(p => parseInt(p, 10) || 0);
    while (parts.length < 3) parts.push(0);
    return parts.slice(0, 3).join('.');
  }

  groupByVersionCode(versions: Version[]): Map<string, Version[]> {
    const groups = new Map<string, Version[]>();

    versions.forEach(version => {
      const key = this.normalizeVersionCode(version.version_code);
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(version);
    });

    groups.forEach(items => {
      items.sort((a, b) => b.version_number - a.version_number);
    });

    const sorted = [...groups.entries()].sort(([a], [b]) => {
      const pa = a.split('.').map(Number);
      const pb = b.split('.').map(Number);
      for (let i = 0; i < 3; i++) {
        if (pa[i] !== pb[i]) return pb[i] - pa[i];
      }
      return 0;
    });

    return new Map(sorted);
  }

  private bumpLevelFor(tipo: VersionTipo, esRuptura: boolean): 'major' | 'minor' | 'patch' {
    if (esRuptura) return 'major';
    if (tipo === 'Nuevo') return 'minor';
    return 'patch';
  }

  suggestNextVersion(latestSemanticCode: string, tipo: VersionTipo, esRuptura: boolean): string {
    const normalized = this.normalizeVersionCode(latestSemanticCode);
    const [major, minor, patch] = normalized.split('.').map(Number);
    const level = this.bumpLevelFor(tipo, esRuptura);
    if (level === 'major') return `${major + 1}.0.0`;
    if (level === 'minor') return `${major}.${minor + 1}.0`;
    return `${major}.${minor}.${patch + 1}`;
  }

  async getMaxSemanticVersionCode(): Promise<string | null> {
    const { data, error } = await supabase
      .from('versiones')
      .select('*');

    if (error || !data || data.length === 0) return null;
    const sorted = data.sort((a, b) => this.compareVersions(a, b));
    return sorted[0].version_code;
  }

  async getLatestVersionCode(): Promise<string | null> {
    const { data, error } = await supabase
      .from('versiones')
      .select('*')
      .eq('publicado', true);

    if (error || !data || data.length === 0) return null;
    const sorted = data.sort((a, b) => this.compareVersions(a, b));
    return sorted[0].version_code;
  }

  async getLatestVersionCreatedAt(): Promise<string | null> {
    const { data, error } = await supabase
      .from('versiones')
      .select('created_at')
      .eq('publicado', true)
      .order('version_number', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error || !data) return null;
    return data.created_at ?? null;
  }

  async getPaginated(
    page: number = 1,
    pageSize: number = 20,
    filters?: VersionFiltersParams
  ): Promise<PaginatedVersionsResponse> {
    let query = supabase
      .from('versiones')
      .select('*', { count: 'exact' })
      .eq('publicado', true);

    if (filters?.searchTerm && filters.searchTerm.trim()) {
      const searchLower = filters.searchTerm.toLowerCase();
      query = query.or(`descripcion.ilike.%${searchLower}%,version_code.ilike.%${searchLower}%,tipo.ilike.%${searchLower}%`);
    }

    if (filters?.tipos && filters.tipos.length > 0) {
      query = query.in('tipo', filters.tipos);
    }

    if (filters?.fechaInicio) {
      query = query.gte('fecha', filters.fechaInicio);
    }

    if (filters?.fechaFin) {
      query = query.lte('fecha', filters.fechaFin);
    }

    const { data, error, count } = await query;

    if (error) throw error;

    const sortedData = (data || []).sort((a, b) => this.compareVersions(a, b));

    const total = count || 0;
    const totalPages = Math.ceil(total / pageSize);

    const from = (page - 1) * pageSize;
    const to = from + pageSize;
    const paginatedData = sortedData.slice(from, to);

    return {
      data: paginatedData,
      total,
      page,
      pageSize,
      totalPages,
    };
  }

  async getVersionById(id: string): Promise<Version | null> {
    const { data, error } = await supabase
      .from('versiones')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) throw error;

    return data;
  }

  async updateVersion(id: string, versionData: UpdateVersionData, originalVersionCode?: string): Promise<{ version: Version; githubUpdated: boolean; githubError?: string }> {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch('/api/actualizar-version', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token ?? ''}`,
      },
      body: JSON.stringify({ id, ...versionData, original_version_code: originalVersionCode }),
    });

    let json: { data?: Version; error?: string; githubUpdated?: boolean; githubError?: string };
    try {
      json = await res.json() as { data?: Version; error?: string; githubUpdated?: boolean; githubError?: string };
    } catch {
      throw new Error('Error al actualizar versión');
    }
    if (!res.ok || json.error) throw new Error(json.error ?? 'Error al actualizar versión');

    return { version: json.data!, githubUpdated: json.githubUpdated ?? false, githubError: json.githubError };
  }

  async deleteVersion(id: string): Promise<void> {
    const { error } = await supabase
      .from('versiones')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  async deleteMultipleVersions(ids: string[]): Promise<void> {
    const { error } = await supabase
      .from('versiones')
      .delete()
      .in('id', ids);

    if (error) throw error;
  }

  async getTotalCountFiltered(filters?: VersionFiltersParams): Promise<number> {
    let query = supabase
      .from('versiones')
      .select('*', { count: 'exact', head: true })
      .eq('publicado', true);

    if (filters?.searchTerm && filters.searchTerm.trim()) {
      const searchLower = filters.searchTerm.toLowerCase();
      query = query.or(`descripcion.ilike.%${searchLower}%,version_code.ilike.%${searchLower}%,tipo.ilike.%${searchLower}%`);
    }

    if (filters?.tipos && filters.tipos.length > 0) {
      query = query.in('tipo', filters.tipos);
    }

    if (filters?.fechaInicio) {
      query = query.gte('fecha', filters.fechaInicio);
    }

    if (filters?.fechaFin) {
      query = query.lte('fecha', filters.fechaFin);
    }

    const { count, error } = await query;

    if (error) return 0;

    return count || 0;
  }

  async getVersionDates(): Promise<{ fecha: string; version_code: string }[]> {
    const { data, error } = await supabase
      .from('versiones')
      .select('fecha, version_code')
      .eq('publicado', true)
      .order('fecha', { ascending: false });

    if (error) throw error;
    return (data ?? []).map(({ fecha, version_code }) => ({
      fecha: fecha.split('T')[0].split(' ')[0],
      version_code,
    }));
  }

  async getVersionesPendientes(): Promise<Version[]> {
    const { data, error } = await supabase
      .from('versiones')
      .select('*')
      .eq('publicado', false)
      .order('fecha', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  async publicarVersionGrupo(versionCode: string): Promise<{ githubReleaseUrl?: string; githubError?: string }> {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch('/api/publicar-version', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token ?? ''}`,
      },
      body: JSON.stringify({ version_code: versionCode }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({})) as { error?: string };
      throw new Error(err.error ?? 'Error al publicar versión');
    }
    return response.json() as Promise<{ githubReleaseUrl?: string; githubError?: string }>;
  }
}

export const versionService = new VersionService();
